<template>
  <div class="h-full">
    <div class="h-full" v-if="editable">
      <loading padding="8" v-if="loading" />
      <form
        class="w-full h-full text-black stack"
        ref="uploadform"
        @dragenter.prevent="toggleUploadVisible(true)"
        @dragend.prevent="toggleUploadVisible(false)"
        @dragleave.prevent="toggleUploadVisible(false)"
        @dragover.prevent
        @drop.stop.prevent="addFile"
        :class="{
          'border border-black rounded flex items-center justify-center text-center': uploadVisible,
        }"
      >
        <input-file
          :class="{ hidden: uploadVisible }"
          @update-value="addFile($event, true)"
          label="Bild hochladen"
        />
        <div
          class="grid grid-cols-fit-250 gap-2"
          :class="{ hidden: uploadVisible }"
          v-if="images"
        >
          <div
            class="w-full border border-black flex justify-center items-center text-black"
            v-if="uploading"
          >
            <loading />
          </div>
          <single-image
            v-for="image in images"
            :key="image.id"
            :image="image"
            :edit="editable"
            @input="selectImage(image)"
          />
        </div>
        <div :class="{ hidden: uploadVisible }">
        <load-more-section show-count name="image" @input="getImages" />
        </div>
        <div v-if="uploadVisible">
          <h2>{{ $t("images.image_upload") }}</h2>
          <p>{{ $t("images.drop_images_to_upload") }}</p>
        </div>
      </form>
    </div>
    <modal @close="emitClose" v-else>
      <loading padding="8" v-if="loading" />
      <form
        class="w-full h-full text-white"
        ref="uploadform"
        @dragenter.prevent="toggleUploadVisible(true)"
        @dragend.prevent="toggleUploadVisible(false)"
        @dragleave.prevent="toggleUploadVisible(false)"
        @dragover.prevent
        @drop.stop.prevent="addFile"
        :class="{
          'border border-white rounded flex items-center justify-center text-center': uploadVisible,
        }"
      >
        <input-file
          class="mb-4"
          @update-value="addFile($event, true)"
          bright
          label="Bild hochladen"
          :class="{ hidden: uploadVisible }"
        />
        <div
          class="grid grid-cols-fit-250 gap-2"
          :class="{ hidden: uploadVisible }"
          v-if="images"
        >
          <div
            class="w-full border border-white flex justify-center items-center text-white"
            v-if="uploading"
          >
            <loading />
          </div>
          <single-image
            v-for="image in images"
            :key="image.id"
            :image="image"
            :edit="editable"
            @input="emitSelection(image)"
          />
        </div>
        <div :class="{ hidden: uploadVisible }">
          <load-more-section :is-last="entityLastPage.image" white show-count name="image" @input="getImages" />
        </div>
        <div v-if="uploadVisible">
          <h2>{{ $t("images.image_upload") }}</h2>
          <p>{{ $t("images.drop_images_to_upload") }}</p>
        </div>
      </form>
    </modal>
    <div v-if="selectedImage">
      <modal @close="selectedImage = null">
        <div class="text-white text-2xl mb-2">
          <wd-button transparent @input="deleteImage">
            <icon name="Trash" />
          </wd-button>
        </div>
        <div class="w-full text-white" v-if="selectedImage">
          <single-image flat :image="selectedImage" />
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import FormMixin from "@/mixins/FormMixin";

const SingleImage = require("@/modules/images/components/SingleImage.vue")
  .default;
const Modal = require("@/modules/core/components/Modal.vue").default;
const LoadMoreSection = require("@/modules/core/components/LoadMoreSection.vue")
  .default;

export default {
  name: "Images",
  mixins: [FormMixin],
  data() {
    return {
      uploadVisible: false,
      uploadCounter: 0,
      fileFormData: new FormData(),
      uploading: false,
      selectedImage: null,
    };
  },
  props: {
    upload: {
      default: false,
      type: Boolean,
    },
    picker: {
      default: false,
      type: Boolean,
    },
    editable: {
      default: false,
      type: Boolean,
    },
  },
  components: {
    SingleImage,
    Modal,
    LoadMoreSection,
  },
  methods: {
    emitClose() {
      this.$emit("close");
    },
    emitSelection(Image) {
      this.$emit("selection", Image.id);
    },
    selectImage(Image) {
      this.selectedImage = Image;
    },
    deleteImage() {
      this.$store.dispatch("dialog/add", {
        title: "Löschen bestätigen",
        text: `Bild wirklich löschen?`,
        submitFn: () => {
          this.submit({
            async: true,
            type: "image",
            call: "destroy",
            id: this.selectedImage.id,
            action: async (response) => {
              this.popup({
                type: "success",
                title: this.$t("images.image_was_deleted"),
                autoHide: true,
              });
              this.selectedImage = false;
            },
          });
        },
      });
    },
    toggleUploadVisible(Bool) {
      if (Bool) {
        this.uploadCounter++;
      } else {
        this.uploadCounter--;
      }
      this.uploadVisible = this.uploadCounter > 0;
    },
    determineDragAndDropCapable() {
      /*
          Create a test element to see if certain events
          are present that let us do drag and drop.
        */
      var div = document.createElement("div");

      /*
          Check to see if the `draggable` event is in the element
          or the `ondragstart` and `ondrop` events are in the element. If
          they are, then we have what we need for dragging and dropping files.

          We also check to see if the window has `FormData` and `FileReader` objects
          present so we can do our AJAX uploading
        */
      return (
        ("draggable" in div || ("ondragstart" in div && "ondrop" in div)) &&
        "FormData" in window &&
        "FileReader" in window
      );
    },
    addFile(e, IsDirectFile) {
      if (this.uploading) {
        this.popup({
          type: "error",
          title: this.$t("images.upload_not_possible"),
          text: this.$t("images.upload_not_possible_message"),
          autohide: true,
        });
        this.uploadVisible = false;
        return;
      }
      this.uploadCounter = 0;
      this.uploadVisible = false;
      this.uploading = true;

      if (IsDirectFile) {
        this.formData.file = e;
      } else {
        this.formData.file = e.dataTransfer.files[0];
      }

      for (let [key, value] of Object.entries(this.formData)) {
        this.fileFormData.append(key, value);
      }

      this.submit(this.formOptions);
    },
    async getImages() {
      await this.getEntity("image");
    },
  },
  computed: {
    uploadingImage() {
      return this.formData.file;
    },
    images(){
      return this.$store.getters["image/images"];
    }
  },
  async created() {
    await this.getImages();

    this.formOptions = {
      async: true,
      type: "image",
      call: "create",
      data: this.fileFormData,
      action: async (response) => {
        this.popup({
          type: "success",
          title: this.$t("images.upload_finished"),
        });
      },
    };

    this.loaded();
  },
  mounted() {
    this.dragAndDropCapable = this.determineDragAndDropCapable();
  },
  watch: {
    images(Val) {
      this.uploading = false;
    },
  },
};
</script>
